import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Select,
  MenuItem,
  FormControl
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TopSection from "../LT/TopSection";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { Loader } from "../../components/common/Loader";
import "./VideoPlayer.css";
import { getAppAdminTrainingTopicSlice } from "../../redux/actions/getAppAdminTrainingTopicSlice";
import { getAppAdminTrainingContentEndpoint } from "../../common/endpoints";
import { fetchSecureVideo } from "../../utils/videoFetcher";
import { getAccessToken } from "../../utils/api";

const TrainingContent = () => {
  const videoRef = useRef(null);
  const [openModal, setOpenModal] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [openPdf, setOpenPdf] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const dispatch = useDispatch();
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [loadVideo, setLoadVideo] = useState(false);
  const [videoBlobUrl, setVideoBlobUrl] = useState("");
  const [pdfBlobUrl, setPdfBlobUrl] = useState("");
  const userEmail = localStorage.getItem("userEmail");
  const { loading } = useSelector((state) => state.getAppAdminTrainingTopicSlice);

  useEffect(() => {
    if (openModal && videoRef.current) {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => { });
      }
    }
  }, [openModal]);

  useEffect(() => {
    return () => {
      if (videoBlobUrl) URL.revokeObjectURL(videoBlobUrl);
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, [videoBlobUrl, pdfBlobUrl]);

  const handleTopicChange = (event) => {
    setSelectedTopic(event.target.value);
  };
  const selectedTopicData = topics.find(
    (t) => t.topic === selectedTopic
  );

  const hasVideo = !!selectedTopicData?.azureBlobName;
  const hasPdf = !!selectedTopicData?.jobAidBlobName;
  const videoUrl = selectedTopicData?.videoUrl;
  
  const pdfList = selectedTopicData
  
    ? [
      {
        name: selectedTopicData.pdfName,
        file: selectedTopicData.pdfUrl,
      },
    ]
    : [];

  useEffect(() => {
    getAllTrainingTopics();
  }, []);

  const getAllTrainingTopics = async () => {
    try {
      const response = await dispatch(
        getAppAdminTrainingTopicSlice({
          dynamicEndpoint: getAppAdminTrainingContentEndpoint
        })
      );

      if (response?.meta?.requestStatus === "fulfilled") {
        const list = response?.payload || [];
        const formatted = list.map((item) => ({
          id: item.topicId,
          topic: item.topicName,
          description: item.description,
          videoTitle: item.videoTitle,
          videoUrl: item.topicId,
          azureBlobName: item.azureBlobName,
          jobAidBlobName: item.jobAidBlobName,
          pdfName: item.jobAidLabel,
          pdfUrl: item.jobAidUrl,
        }));
        setTopics(formatted);
        // auto select first topic
        if (formatted.length > 0) {
          setSelectedTopic(formatted[0].topic);
        }
      }
    } catch (error) {
      console.error("GET TOPICS ERROR", error);
    }
  };

  useEffect(() => {
    setIsPlaying(false);
    setProgress(0);

    if (videoRef.current) {
      videoRef.current.pause();
    }
  }, [selectedTopic]);

  const togglePlay = () => {
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const forward = () => {
    const video = videoRef.current;
    if (!video) return;

    const safeDuration = video.duration || 0;
    if (!isFinite(safeDuration)) return;

    const newTime = Math.min(video.currentTime + 10, safeDuration);

    video.pause(); // important
    video.currentTime = newTime;
    setProgress(newTime);
  };

  const backward = () => {
    const video = videoRef.current;
    if (!video) return;

    const newTime = Math.max(video.currentTime - 10, 0);

    video.pause(); // important
    video.currentTime = newTime;
    setProgress(newTime);
  };

  const handleTimeUpdate = () => {
    setProgress(videoRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    setDuration(videoRef.current.duration);
  };

  const handleSeek = (e) => {
    const time = Number(e.target.value);
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = time;
    setProgress(time);
  };

  const handleVolume = (e) => {
    videoRef.current.volume = e.target.value;
    setVolume(e.target.value);
  };

  const fullScreen = async () => {
    const video = videoRef.current;

    if (!video) return;

    try {
      if (video.requestFullscreen) {
        await video.requestFullscreen();
      } else if (video.webkitRequestFullscreen) {
        await video.webkitRequestFullscreen();
      }
    } catch (err) {
      console.log("Fullscreen error:", err);
    }
  };

  const formatTime = (t) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const handleClose = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }

    setIsPlaying(false);
    setProgress(0);
    setDuration(0);

    if (videoBlobUrl) {
      URL.revokeObjectURL(videoBlobUrl);
      setVideoBlobUrl("");
    }

    setOpenModal(false);
  };

  const handleOpenPdf = async () => {
    try {
      setPdfLoading(true);

      const selected = topics.find(t => t.topic === selectedTopic);
      if (!selected) return;

      const accessToken = await getAccessToken();

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v1/training/content/pdf/${selected.id}?userEmail=${encodeURIComponent(userEmail)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("PDF fetch failed");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      setPdfBlobUrl(url);

      setSelectedPdf({
        name: selected.pdfName,
        file: url,
      });

      setOpenPdf(true);
    } catch (err) {
      console.error(err);
      toast.error("Unable to load PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  const handleClosePdf = () => {
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl("");
    }

    setSelectedPdf(null);
    setOpenPdf(false);
  };

  // const downloadVideo = async () => {
  //   try {
  //     const selected = topics.find(t => t.topic === selectedTopic);
  //     if (!selected) return;

  //     const accessToken = await getAccessToken();

  //     const response = await fetch(
  //       `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v1/training/content/${selected.id}`,
  //       {
  //         method: "GET",
  //         headers: {
  //           Authorization: `Bearer ${accessToken}`,
  //         },
  //       }
  //     );

  //     if (!response.ok) throw new Error("Download failed");

  //     const blob = await response.blob();
  //     const url = window.URL.createObjectURL(blob);

  //     const a = document.createElement("a");
  //     a.href = url;
  //     a.download = selected.videoTitle || "video.mp4";

  //     document.body.appendChild(a);
  //     a.click();
  //     a.remove();

  //     window.URL.revokeObjectURL(url);
  //   } catch (err) {
  //     console.error(err);
  //     toast.error("Video download failed");
  //   }
  // };

  const handlePdfDownload = async () => {
    if (!selectedPdf?.file) return;

    try {
      const response = await fetch(selectedPdf.file);
      if (!response.ok) throw new Error("Fetch failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = selectedPdf?.name;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log("CORS blocked → fallback");

      // fallback MUST use new tab
      window.open(selectedPdf.file, "_blank");
    }
  };

  const isEmpty = topics.length === 0;
  const handleOpenVideo = async () => {
    try {
      setVideoLoading(true);

      const selected = topics.find(t => t.topic === selectedTopic);
      if (!selected) return;
      const accessToken = await getAccessToken(); // reuse your MSAL function

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v1/training/content/video/${selected.id}?userEmail=${encodeURIComponent(userEmail)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) throw new Error("Video fetch failed");

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      setVideoBlobUrl(url);   // ✅ now defined correctly
      setOpenModal(true);

    } catch (err) {
      console.error(err);
      toast.error("Unable to load video");
    } finally {
      setVideoLoading(false);
    }
  };

  const isVideoReady = isFinite(duration) && duration > 0;

  return (
    <Box>
      <TopSection
        labelList={[
          { title: "Home", path: "/dashboard" },
          { title: "Training Content" },
        ]}
        headerName="Training Content"
        hideHeader
      />
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Loader />
        </Box>
      ) : isEmpty ? (
        <Typography color="textSecondary" paddingBottom={5} gap={2}>
          No training content available
        </Typography>) : (
        <>

          <Box display="flex" alignItems="center" paddingBottom={5} gap={2}>
            <Typography variant="body1" sx={{ minWidth: 60 }}>
              Topic
            </Typography>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={selectedTopic}
                onChange={handleTopicChange}
                displayEmpty
              >
                {(topics || []).map((t) => (
                  <MenuItem key={t.id} value={t.topic}>
                    {t.topic}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Grid container spacing={3} padding={5}>
            {/* LEFT SIDE - VIDEO */}
            <Grid item xs={12} md={8}>
              <Card sx={{ p: 2 }} sx={{
                border: "3px solid #e0e0e0",
                borderRadius: 2,
                padding: 2
              }}>
                <Typography variant="h6" mb={1}>
                  {selectedTopic}
                </Typography>

                <Box
                  sx={{
                    height: 300,
                    border: "1px dashed #ccc",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {!hasVideo ? (
                    <Typography color="text.secondary">
                      No video available
                    </Typography>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleOpenVideo}
                      disabled={videoLoading}
                      sx={{
                        backgroundColor: "black",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "#333",
                        },
                      }}
                    >
                      {videoLoading ? "Loading..." : "Open Video"}
                    </Button>
                  )}
                </Box>
              </Card>
            </Grid>

            {/* RIGHT SIDE - PDF */}
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 2 }} sx={{
                border: "3px solid #e0e0e0",
                borderRadius: 2,
                padding: 2
              }}>
                <Typography variant="h6" mb={2}>
                  Supporting Material
                </Typography>

                <Typography variant="body2" sx={{ mb: 2 }}>
                  Job aids, PDFs and guided links for the selected topic.
                </Typography>

                {/* BORDER CARD */}
                <Card
                  variant="outlined"
                  sx={{
                    border: "2px solid #e0e0e0",
                    borderRadius: 2,
                  }}
                >
                  <List>
                    {hasPdf ? (
                      pdfList.map((pdf, index) => (
                        <ListItem
                          key={index}
                          secondaryAction={
                            <Button
                              size="small"
                              onClick={handleOpenPdf}
                              disabled={pdfLoading}
                            >
                              {pdfLoading ? "Loading..." : "Open"}
                            </Button>
                          }
                        >
                          <ListItemText
                            primary={pdf.name}
                            secondary="Job Aid"
                          />
                        </ListItem>
                      ))
                    ) : (
                      <Typography color="text.secondary" sx={{ p: 2 }}>
                        No PDF available
                      </Typography>
                    )}
                  </List>
                </Card>
              </Card>
            </Grid>
          </Grid>

          {/* VIDEO MODAL */}
          <Dialog
            open={openModal}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
          >
            {/* Header */}
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {selectedTopic}

              <Box>
                {/* Close */}
                <IconButton onClick={handleClose}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            {/* Video */}
            <DialogContent>
              <Box className="video-wrapper">
                {videoLoading && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      color: "#fff",
                      zIndex: 10,
                    }}
                  >
                    <Loader />
                  </Box>
                )}
                <video
                  ref={videoRef}
                  className="video"
                  preload="metadata"
                  playsInline
                  onWaiting={() => setVideoLoading(true)}
                  onCanPlay={() => {
                    setVideoLoading(false);
                    videoRef.current?.play();
                    setIsPlaying(true);
                  }}
                  onSeeking={() => setVideoLoading(true)}
                  onSeeked={() => setVideoLoading(false)}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                >
                  <source src={videoBlobUrl} type="video/mp4" />
                </video>


                {/* CONTROLS */}
                <Box className="controls">

                  {/* Progress Bar */}
                  <input
                    type="range"
                    min="0"
                    max={duration}
                    value={progress}
                    onChange={handleSeek}
                    className="progress"
                  />

                  <Box className="bottom-controls">

                    <button onClick={backward}>⏪</button>

                    <button onClick={togglePlay}>
                      {isPlaying ? "⏸" : "▶"}
                    </button>

                    <button onClick={forward}>⏩</button>

                    {/* Time */}
                    <span>
                      {formatTime(progress)} / {formatTime(duration)}
                    </span>

                    {/* Volume */}
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={volume}
                      onChange={handleVolume}
                    />

                    <div style={{ flex: 1 }} />

                    {/* Download */}
                    {/* <button onClick={downloadVideo}>⬇</button> */}

                    {/* Fullscreen */}
                    <button onClick={fullScreen}>⛶</button>

                  </Box>
                </Box>

              </Box>

            </DialogContent>

          </Dialog>

          <Dialog
            open={openPdf}
            onClose={handleClosePdf}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {selectedPdf?.name}

              <Box>
                {/* Download */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handlePdfDownload}
                  sx={{ mr: 1 }}
                >
                  Download
                </Button>


                {/* Close */}
                <IconButton onClick={handleClosePdf}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent>
              {selectedPdf && (
                <iframe
                  src={selectedPdf?.file}
                  title="PDF Viewer"
                  width="100%"
                  height="500px"
                  style={{ border: "none" }}
                />
              )}
            </DialogContent>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default TrainingContent;
